import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Globe,
  Mail,
  Phone,
  MapPin,
  Tag,
  Lock,
  QrCode,
  ExternalLink,
  Package,
  Bookmark,
  BookmarkCheck,
  Users,
  ChevronRight,
  Building2,
  Flag,
  CalendarDays,
  Info,
  UserCheck,
} from "lucide-react";
import { motion } from "motion/react";
import { useAppStore } from "../store/useAppStore";
import { companiesService } from "../services/companiesService";
import { catalogService } from "../services/catalogService";
import type { Company, Product, Representative, Service } from "../types";
import { maxPlatform } from "../services/maxPlatform";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { CompanyAvatar } from "../components/CompanyAvatar";
import { RepresentativeCard } from "../components/RepresentativeCard";
import { ProductCard } from "../components/ProductCard";
import { toast } from "sonner";
import { formatBoothLabel, formatCompanyLocation } from "../utils/locationLabel";
import { formatCategoriesDisplay } from "../utils/categoryLabel";
import { engagementService } from "../services/engagementService";
import { useTranslation } from "react-i18next";
import { QuickAnnotatePanel } from "../components/QuickAnnotatePanel";
import { scanApi } from "../api/scan";
import { SkeletonList } from "../components/SkeletonList";
import {
  newAnnotationNote,
  parseAnnotationNotes,
  serializeAnnotationNotes,
  type AnnotationNote,
} from "../utils/annotationNotes";
import { markLabel, CONTACT_MARK_TONE_CLASS, CONTACT_MARK_PRESETS } from "../utils/contactMarks";

export function CompanyDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    isContactUnlocked,
    isRepresentativeUnlocked,
    savedContacts,
    saveContact,
    updateSavedContact,
    updateScan,
    selectedEventId,
    user,
  } = useAppStore();
  const [company, setCompany] = useState<Company | undefined>();
  const [loading, setLoading] = useState(true);
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [noteEntries, setNoteEntries] = useState<AnnotationNote[]>([]);
  const [scanAnnotation, setScanAnnotation] = useState<{
    scanId?: string;
    tags: string[];
  }>({ tags: [] });
  const [services, setServices] = useState<Service[]>([]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const found = await companiesService.getById(id, selectedEventId ?? undefined);
        if (!cancelled) setCompany(found);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, selectedEventId, i18n.language]);

  useEffect(() => {
    if (!company || !selectedEventId) {
      setScanAnnotation({ tags: [] });
      setNoteEntries([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { scans } = await scanApi.list(selectedEventId);
        if (cancelled) return;
        const hit = scans.find(
          (s) =>
            s.companyId === company.id ||
            (s.scanTargetType === "company" && s.scanTargetId === company.id) ||
            (s.scanTargetType === "booth" && s.companyId === company.id),
        );
        const local = savedContacts.find((c) => c.companyId === company.id);
        const rawNotes = hit?.notes ?? local?.notes ?? "";
        const tags = hit?.tags ?? local?.tags ?? [];
        setScanAnnotation({
          scanId: hit?.id,
          tags,
        });
        setNoteEntries(parseAnnotationNotes(rawNotes, tags));
      } catch {
        if (!cancelled) {
          const local = savedContacts.find((c) => c.companyId === company.id);
          const tags = local?.tags ?? [];
          setScanAnnotation({ tags });
          setNoteEntries(parseAnnotationNotes(local?.notes, tags));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, selectedEventId, savedContacts]);

  useEffect(() => {
    if (!company || !selectedEventId) return;
    void engagementService.trackCompanyView(selectedEventId, company.id);
  }, [company?.id, selectedEventId]);

  useEffect(() => {
    if (!company || !selectedEventId) {
      setRepresentatives([]);
      setProducts([]);
      setServices([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const [reps, prods, srvs] = await Promise.all([
        catalogService.listRepresentatives(selectedEventId, company.id),
        catalogService.listProducts(selectedEventId, company.id),
        catalogService.listServices(selectedEventId, company.id),
      ]);
      if (!cancelled) {
        setRepresentatives(reps);
        setProducts(prods);
        setServices(srvs);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, selectedEventId, i18n.language]);

  const unlocked = company ? isContactUnlocked(company.id) : false;
  const savedContact = company
    ? savedContacts.find((c) => c.companyId === company.id)
    : undefined;
  const isSaved = Boolean(savedContact);
  const unlockedReps = representatives.filter((r) => isRepresentativeUnlocked(r.id));
  const lockedReps = representatives.filter((r) => !isRepresentativeUnlocked(r.id));
  const lockedRoleSummary = [
    ...new Set(
      lockedReps
        .map((r) => (r.position ?? "").trim())
        .filter(Boolean),
    ),
  ];
  const roleSummary = [
    ...new Set(
      representatives
        .map((r) => (r.position ?? "").trim())
        .filter(Boolean),
    ),
  ];

  if (loading) {
    return (
      <>
        <PageHeader title={t("companies.detail_title")} back />
        <PageShell>
          <SkeletonList variant="detail" />
        </PageShell>
      </>
    );
  }

  if (!company) {
    return (
      <>
        <PageHeader title={t("companies.detail_title")} back />
        <PageShell>
          <div className="pt-20 text-center text-gray-500">{t("companies.not_found")}</div>
        </PageShell>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={company.name}
        subtitle={
          formatCompanyLocation({
            booth: company.booth,
            hall: company.hall,
            hideHall: !company.booth?.trim(),
          }) || undefined
        }
        back
      />
      <PageShell>
        <div className="mt-4 space-y-4">
          {/* Company header */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <GlassCard variant="elevated" padding="lg">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-4">
                  <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size="2xl" />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-[20px] text-gray-900 leading-snug">
                      {company.name}
                    </h2>
                    {formatBoothLabel(company.booth) ? (
                      <p className="text-[13px] text-gray-500 mt-0.5">
                        {formatBoothLabel(company.booth)}
                      </p>
                    ) : null}                  </div>
                </div>

                <div className="min-w-0 space-y-1">
                  {formatCategoriesDisplay(company.category) && (
                    <p className="text-[13px] text-indigo-500">
                      {formatCategoriesDisplay(company.category)}
                    </p>
                  )}
                  {(company.country || company.city) && (
                    <div className="flex items-center gap-1 text-[12px] text-gray-400">
                      <Flag size={12} />
                      <span>
                        {[company.city, company.country].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick stats row */}
              {(company.foundedYear || company.employeesCount) && (
                <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                  {company.foundedYear && (
                    <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
                      <CalendarDays size={13} className="text-gray-400" />
                      <span>{t("visitor.founded", { year: company.foundedYear })}</span>
                    </div>
                  )}
                  {company.employeesCount && (
                    <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
                      <Users size={13} className="text-gray-400" />
                      <span>{t("visitor.employees", { count: Number(company.employeesCount) })}</span>
                    </div>
                  )}
                </div>
              )}
            </GlassCard>
          </motion.div>

          {/* About */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.35 }}
          >
            <GlassCard variant="default">
              <h3 className="text-[14px] text-gray-900 mb-2">{t("companies.about")}</h3>
              <p className="text-[14px] text-gray-600 leading-relaxed">
                {company.shortAbout}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {company.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-indigo-50 text-[11px] text-indigo-600"
                  >
                    <Tag size={10} />
                    {tag}
                  </span>
                ))}
              </div>
            </GlassCard>
          </motion.div>

          {/* Notes only after company/booth QR scan. */}
          {unlocked && scanAnnotation.scanId ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.11, duration: 0.35 }}
            >
              <GlassCard variant="default">
                {noteEntries.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {noteEntries.map((note) => (
                      <div
                        key={note.id}
                        className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-3 py-2.5"
                      >
                        {note.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {note.tags.map((tagId) => {
                              const preset = CONTACT_MARK_PRESETS.find((p) => p.id === tagId);
                              const tone = preset
                                ? CONTACT_MARK_TONE_CLASS[preset.tone]
                                : CONTACT_MARK_TONE_CLASS.slate;
                              return (
                                <span
                                  key={tagId}
                                  className={`px-2 py-0.5 rounded-full border text-[11px] ${tone.on}`}
                                >
                                  {markLabel(tagId, t)}
                                </span>
                              );
                            })}
                          </div>
                        ) : null}
                        {note.text ? (
                          <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{note.text}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                <QuickAnnotatePanel
                  key={`${company.id}-${scanAnnotation.scanId ?? "local"}-composer`}
                  initialNotes=""
                  initialTags={[]}
                  title={t("annotate.title")}
                  placeholder={t("annotate.placeholder")}
                  saveLabel={t("annotate.save")}
                  onSave={async (values) => {
                    if (!selectedEventId || !scanAnnotation.scanId) {
                      toast.error(t("annotate.save_failed"));
                      throw new Error("scan_required");
                    }
                    const hitId = scanAnnotation.scanId;
                    try {
                      const nextNotes = [
                        ...noteEntries,
                        newAnnotationNote(values.notes, values.tags),
                      ];
                      const serialized = serializeAnnotationNotes(nextNotes);
                      const allTags = [...new Set(nextNotes.flatMap((n) => n.tags))];
                      await scanApi.updateNotes(selectedEventId, hitId, {
                        notes: serialized,
                        tags: allTags,
                      });
                      updateSavedContact(company.id, {
                        notes: serialized,
                        tags: allTags,
                        eventId: selectedEventId,
                        userId: user.id,
                      });
                      updateScan(hitId, { notes: serialized, tags: allTags });
                      setScanAnnotation({ scanId: hitId, tags: allTags });
                      setNoteEntries(nextNotes);
                      toast.success(t("annotate.saved"));
                    } catch (err) {
                      toast.error(t("annotate.save_failed"));
                      throw err;
                    }
                  }}
                />
              </GlassCard>
            </motion.div>
          ) : null}

          {/* Representatives — FIO only for scanned badges; others: roles + scan CTA */}
          {representatives.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12, duration: 0.35 }}
            >
              <GlassCard variant="default">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[14px] text-gray-900">
                    {t("visitor.representatives", { count: representatives.length })}
                  </h3>
                </div>

                {unlockedReps.length > 0 ? (
                  <div className="space-y-2">
                    {unlockedReps.map((rep) => (
                      <RepresentativeCard key={rep.id} representative={rep} compact />
                    ))}
                  </div>
                ) : null}

                {lockedReps.length > 0 ? (
                  <div className={unlockedReps.length > 0 ? "mt-3 space-y-3" : "space-y-3"}>
                    {(unlockedReps.length > 0 ? lockedRoleSummary : roleSummary).length > 0 ? (
                      <p className="text-[13px] text-gray-600 leading-snug">
                        {(unlockedReps.length > 0 ? lockedRoleSummary : roleSummary).join(", ")}
                      </p>
                    ) : unlockedReps.length === 0 ? (
                      <p className="text-[13px] text-gray-500">
                        {t("visitor.representatives", { count: representatives.length })}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        maxPlatform.hapticFeedback("light");
                        navigate("/scan");
                      }}
                      className="w-full h-10 rounded-xl bg-indigo-600 text-white text-[14px] font-medium flex items-center justify-center gap-2"
                    >
                      <QrCode size={16} />
                      {t("scan.scan_action", { defaultValue: "Сканировать" })}
                    </button>
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50/70 border border-amber-100">
                      <UserCheck size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-[12px] text-amber-700 leading-snug">
                        {t("visitor.personal_contact_hint")}
                      </p>
                    </div>
                  </div>
                ) : null}
              </GlassCard>
            </motion.div>
          )}

          {/* Products & Services */}
          {(products.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14, duration: 0.35 }}
            >
              <button
                onClick={() => navigate(`/companies/${company.id}/products`)}
                className="w-full"
              >
                <GlassCard variant="default" className="hover:border-indigo-200 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center flex-shrink-0">
                      <Package size={20} className="text-amber-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-[14px] text-gray-900 mb-0.5">{t("companies.products", { defaultValue: "Товары" })}</h3>
                      <p className="text-[12px] text-gray-500">
                        {t("visitor.items_count", {
                          defaultValue: "{{n}} позиций",
                          n: products.length,
                        })}
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-gray-400" />
                  </div>
                </GlassCard>
              </button>
            </motion.div>
          )}

          {/* General contacts — unlocked after scanning company QR */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.35 }}
          >
            <GlassCard variant="default">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-[14px] text-gray-900">{t("visitor.general_contacts")}</h3>
                {!unlocked && (
                  <span className="flex items-center gap-1 text-[11px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                    <Lock size={10} />
                    {t("visitor.qr_required")}
                  </span>
                )}
              </div>

              {/* Subtitle explaining these are general contacts */}
              <p className="text-[12px] text-gray-400 mb-3 leading-snug">
                {unlocked
                  ? t("visitor.reception_line", { title: company.contactPersonTitle ?? t("visitor.reception") })
                  : t("visitor.scan_booth_qr_contacts")}
              </p>

              {/* Always visible: website */}
              {company.website && (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[13px] text-gray-600 hover:text-indigo-500 mb-2"
                >
                  <Globe size={16} className="text-gray-400" />
                  <span className="truncate">{company.website.replace("https://", "")}</span>
                  <ExternalLink size={12} className="text-gray-300 flex-shrink-0" />
                </a>
              )}

              {unlocked ? (
                <div className="space-y-2">
                  {company.contact.email && (
                    <a
                      href={`mailto:${company.contact.email}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Mail size={16} className="text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-gray-400 mb-0.5">Email</div>
                        <div className="text-[13px] text-gray-700 truncate">{company.contact.email}</div>
                      </div>
                    </a>
                  )}
                  {company.contact.phone && (
                    <a
                      href={`tel:${company.contact.phone}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Phone size={16} className="text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-gray-400 mb-0.5">{t("visitor.phone")}</div>
                        <div className="text-[13px] text-gray-700">{company.contact.phone}</div>
                      </div>
                    </a>
                  )}

                  {/* Explanatory note — no in-app chat for general contacts */}
                  <div className="flex items-start gap-2 mt-1 p-2.5 rounded-xl bg-gray-50 border border-gray-100">
                    <Info size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-gray-500 leading-snug">
                      {t("visitor.general_contact_note")}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <Lock size={22} className="text-gray-400" />
                  </div>
                  <p className="text-[13px] text-gray-500 mb-3">
                    {t("visitor.scan_booth_qr", { booth: company.booth ?? "" })}
                  </p>
                  <button
                    onClick={() => {
                      maxPlatform.hapticFeedback("medium");
                      navigate("/scan", { state: { companyId: company.id } });
                    }}
                    className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-indigo-500 text-white text-[14px] active:bg-indigo-600 transition-colors shadow-lg shadow-indigo-500/20"
                  >
                    <QrCode size={18} />
                    {t("visitor.scan_booth")}
                  </button>
                </div>
              )}
            </GlassCard>
          </motion.div>

          {/* Map shortcut */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.35 }}
          >
            <button
              onClick={() => navigate("/map", { state: { highlightCompanyId: company.id } })}
              className="w-full"
            >
              <GlassCard
                variant="subtle"
                padding="sm"
                className="active:scale-[0.98] transition-transform"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <MapPin size={20} className="text-blue-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-[14px] text-gray-900">{t("companies.show_on_map")}</p>
                    <p className="text-[12px] text-gray-500">
                      {formatCompanyLocation(company) || t("visitor.open_exhibition_map")}
                    </p>
                  </div>
                  <ExternalLink size={16} className="text-gray-300" />
                </div>
              </GlassCard>
            </button>
          </motion.div>

          {/* Save contact */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.35 }}
          >
            <button
              onClick={() => {
                if (isSaved) {
                  toast.info(t("visitor.contact_already_saved"));
                } else {
                  maxPlatform.hapticFeedback("medium");
                  saveContact({
                    id: `sc-${Date.now()}`,
                    userId: "user-1",
                    eventId: company.eventId,
                    companyId: company.id,
                    savedAt: new Date().toISOString(),
                  });
                  toast.success(t("visitor.contact_saved"));
                }
              }}
              className="w-full"
            >
              <GlassCard
                variant="subtle"
                padding="sm"
                className="active:scale-[0.98] transition-transform"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSaved ? "bg-emerald-100" : "bg-indigo-100"}`}>
                    {isSaved ? <BookmarkCheck size={20} className="text-emerald-500" /> : <Bookmark size={20} className="text-indigo-500" />}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-[14px] text-gray-900">
                      {isSaved ? t("visitor.contact_saved") : t("companies.save_company")}
                    </p>
                    <p className="text-[12px] text-gray-500">
                      {isSaved ? t("companies.in_contacts") : t("companies.add_favorite")}
                    </p>
                  </div>
                </div>
              </GlassCard>
            </button>
          </motion.div>
        </div>
      </PageShell>
    </>
  );
}
